import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import type { SxProps } from '@mui/material/styles';
import { ArrowRight as ArrowRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowRight';
import { ArrowCircleRight as OpenIcon } from '@phosphor-icons/react/dist/ssr/ArrowCircleRight';
import { Avatar, Typography, Tooltip, CircularProgress } from '@mui/material';
import { WorkflowTasks } from '../../types/ProgramTypes';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../utils/paths';
import AssignmentIcon from '@mui/icons-material/Assignment';

export interface RecentWorkflowsProps {
  workflows?: WorkflowTasks[];
  sx?: SxProps;
  loading?: boolean;
}

export function RecentWorkflows({ workflows = [], sx, loading = false }: RecentWorkflowsProps): React.JSX.Element {
  const navigate = useNavigate();

  const handleTaskClick = (assignmentId: number) => {
    navigate(paths.business.workflowView, {
      state: { assignmentId: assignmentId }
    });
  };

  const handleViewAll = () => {
    navigate(paths.business.workflows);
  };

  return (
    <Card sx={sx}>
      <CardHeader title="Workflows & Tasks" />
      <Divider />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={40} />
        </Box>
      ) : workflows.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No active tasks found
          </Typography>
        </Box>
      ) : (
        <List>
          {workflows.map((workflow, index) => (
            <ListItem divider={index < workflows.length - 1} key={workflow.trackerId}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssignmentIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={workflow.workflowName + ' : ' + workflow.programName}
                primaryTypographyProps={{ variant: 'subtitle1' }}
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'text.primary', display: 'inline' }}
                    >
                      {workflow.taskOn}
                    </Typography>
                    {" : " + workflow.task}
                  </React.Fragment>
                }
                secondaryTypographyProps={{ variant: 'body2' }}
              />
              <Tooltip title="View Task">
                <IconButton 
                  edge="end"
                  onClick={() => handleTaskClick(workflow.assignmentId)}
                >
                  <OpenIcon weight="bold" />
                </IconButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      )}
      <Divider />
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          color="inherit"
          endIcon={<ArrowRightIcon fontSize="var(--icon-fontSize-md)" />}
          size="small"
          variant="text"
          onClick={handleViewAll}
        >
          View all
        </Button>
      </CardActions>
    </Card>
  );
}
